import CART_ITEM from '../../../../../../../services/checkout/constants/cartItems';
import { getPrintPackageCartItemNonConfiguredItems } from '../PurchasingOptions/components/AlbumPrintPackageOption/helpers';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../../../../constants/print-packages';

/**
 * Determines if the identity a print package was purchased for has matches
 *
 * @param {Object} purchasedPrintPackage the purchased Print Package
 * @param {Array} albumMembershipIdentities the membership identities
 * @returns {Boolean}
 */
export const purchasedPrintPackageIdentityHasMatches = (
  purchasedPrintPackage,
  albumMembershipIdentities,
) => {
  if (!purchasedPrintPackage) {
    return false;
  }

  // Assume there is matches by default.
  if (!albumMembershipIdentities) {
    return true;
  }

  const { identity } = purchasedPrintPackage;

  return (
    (albumMembershipIdentities.find(
      albumMembershipIdentity =>
        albumMembershipIdentity.identity &&
        albumMembershipIdentity.identity.id === identity?.id,
    )?.matched_photos_extended?.total ?? 0) > 0
  );
};

/**
 * Retrieves the Purchased Print Packages that are yet to be added to cart
 *
 * @param {Array} cart the current cart
 * @param {Array} purchasedPrintPackages the purchased Print Packages
 * @returns {Array}
 */
export const getPurchasedPrintPackagesMissingConfiguration = (
  cart,
  purchasedPrintPackages,
) => {
  if (!cart || !purchasedPrintPackages) {
    return [];
  }

  const purchasedPrintPackagesItems = cart.filter(
    item => item.type === CART_ITEM.PURCHASED_PRINT_PACKAGE,
  );

  return purchasedPrintPackages.filter(purchasedPrintPackage => {
    const cartItem = purchasedPrintPackagesItems.find(
      item => item.purchasedPrintPackage.id === purchasedPrintPackage.id,
    );

    if (!cartItem) {
      return true;
    }

    if (getPrintPackageCartItemNonConfiguredItems(cartItem).length !== 0) {
      return true;
    }

    const {
      albumPrintPackage,
      isGreenScreenEnabled,
      digitalDownloadAccountCard,
    } = cartItem;

    const { print_package: printPackage } = albumPrintPackage ?? {};

    // Special green screen only condition. On green screen w/ digital downloads includded,
    // user must have selected a digital download account card before proceeding.
    return (
      isGreenScreenEnabled &&
      printPackage.digital_downloads_mode === DIGITAL_DOWNLOADS_MODE.INCLUDED &&
      !digitalDownloadAccountCard
    );
  });
};

/**
 * Retrieves the Purchased Photo Pricings that are yet to be added to cart
 *
 * @param {Array} cart the current cart
 * @param {Array} purchasedPhotoPricings the purchased Photo Pricings
 * @returns {Array}
 */
export const getPurchasedPhotoPricingsMissingConfiguration = (
  cart,
  purchasedPhotoPricings,
) => {
  if (!cart || !purchasedPhotoPricings) {
    return [];
  }

  const purchasedPhotoPricingsPrintProductItems = cart.filter(item =>
    [
      CART_ITEM.DIGITAL_DOWNLOAD,
      CART_ITEM.PURCHASED_DIGITAL_DOWNLOAD,
      CART_ITEM.PURCHASED_PHOTO_PRICING_PRINT_PRODUCT,
    ].includes(item.type),
  );

  return purchasedPhotoPricings.filter(purchasedPhotoPricing => {
    const cartItem = purchasedPhotoPricingsPrintProductItems.find(
      item => item.purchasedPhotoPricing?.id === purchasedPhotoPricing.id,
    );

    return !cartItem;
  });
};

/**
 * Retrieves the Purchased Custom Prints that are yet to be added to cart
 *
 * @param {Array} cart the current cart
 * @param {Array} purchasedCustomPrints the purchased Custom Prints
 * @returns {Array}
 */
export const getPurchasedCustomPrintsMissingConfiguration = (
  cart,
  purchasedCustomPrints,
) => {
  if (!cart || !purchasedCustomPrints) {
    return [];
  }

  const purchasedCustomPrintsPrintProductItems = cart.filter(item =>
    [CART_ITEM.PURCHASED_CUSTOM_PRINT].includes(item.type),
  );

  return purchasedCustomPrints.filter(purchasedCustomPrint => {
    const cartItem = purchasedCustomPrintsPrintProductItems.find(
      item => item.purchasedCustomPrint?.id === purchasedCustomPrint.id,
    );

    return !cartItem;
  });
};

/**
 * Retrieves the Purchased Print Packages that are configurable due to having matches
 *
 * @param {Array} purchasedPrintPackages the purchased Print Packages
 * @param {Array} albumMembershipIdentities the membership identities
 * @returns {Array}
 */
export const getPurchasedPrintPackagesWithMatches = (
  purchasedPrintPackages,
  albumMembershipIdentities,
) => {
  if (!purchasedPrintPackages) {
    return [];
  }

  if (!albumMembershipIdentities) {
    return purchasedPrintPackages;
  }

  return purchasedPrintPackages.filter(purchasedPrintPackage =>
    purchasedPrintPackageIdentityHasMatches(
      purchasedPrintPackage,
      albumMembershipIdentities,
    ),
  );
};

/**
 * Retrieves the Purchased Print Packages that are not configurable due to having no matches
 *
 * @param {Array} purchasedPrintPackages the purchased Print Packages
 * @param {Array} albumMembershipIdentities the membership identities
 * @returns {Array}
 */
export const getPurchasedPrintPackagesWithoutMatches = (
  purchasedPrintPackages,
  albumMembershipIdentities,
) => {
  if (!purchasedPrintPackages || !albumMembershipIdentities) {
    return [];
  }

  return purchasedPrintPackages.filter(
    purchasedPrintPackage =>
      !purchasedPrintPackageIdentityHasMatches(
        purchasedPrintPackage,
        albumMembershipIdentities,
      ),
  );
};
